import React, { useEffect, useState } from 'react';
import { AlertCircle, ArrowRight, Calendar, LogOut, Plus, Trash2, TrendingUp, User } from 'lucide-react';
import { createEvent, createLimitRequest, deleteEvent, getEvents, logout } from '../api';
import type { Event, User as UserType } from '../types';

interface EventDashboardProps {
  user: Omit<UserType, 'passwordHash'>;
  onEventSelect: (event: Event) => void;
  onLogout: () => void;
}

const formatDisplayDate = (dateString: string) =>
  new Date(dateString).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

export default function EventDashboard({ user, onEventSelect, onLogout }: EventDashboardProps) {
  const currentEventLimit = user.eventLimit || 20;
  const currentGuestLimit = user.guestLimit || 20;

  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newEventName, setNewEventName] = useState('');
  const [newEventDescription, setNewEventDescription] = useState('');
  const [newEventDate, setNewEventDate] = useState('');
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);

  const [showLimitRequestForm, setShowLimitRequestForm] = useState(false);
  const [requestedEventLimit, setRequestedEventLimit] = useState(currentEventLimit + 1);
  const [requestedGuestLimit, setRequestedGuestLimit] = useState(currentGuestLimit + 10);
  const [requestMessage, setRequestMessage] = useState('');
  const [requestError, setRequestError] = useState('');
  const [requestingLimit, setRequestingLimit] = useState(false);

  useEffect(() => {
    loadEvents();
  }, []);

  const loadEvents = async () => {
    setLoading(true);

    try {
      const response = await getEvents();
      if (response.success && response.events) {
        setEvents(response.events);
      }
    } catch (err) {
      console.error('Failed to load events:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEvent = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (events.length >= currentEventLimit) {
      setError(`You have reached your maximum limit of ${currentEventLimit} event${currentEventLimit > 1 ? 's' : ''}. Delete an existing event to create a new one.`);
      return;
    }

    if (!newEventName.trim()) {
      setError('Event name is required');
      return;
    }

    setCreating(true);

    try {
      const response = await createEvent({
        name: newEventName.trim(),
        description: newEventDescription.trim() || undefined,
        date: newEventDate || undefined,
      });

      if (response.success && response.event) {
        setEvents([response.event, ...events]);
        setShowCreateForm(false);
        setNewEventName('');
        setNewEventDescription('');
        setNewEventDate('');
      } else {
        setError(response.error || 'Failed to create event');
      }
    } catch (err) {
      setError('An error occurred while creating the event');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteEvent = async (eventId: string) => {
    if (!confirm('Are you sure you want to delete this event? All guests and floor plan data will be permanently deleted.')) {
      return;
    }

    try {
      const response = await deleteEvent(eventId);
      if (response.success) {
        setEvents(events.filter((event) => event.id !== eventId));
      } else {
        alert(response.error || 'Failed to delete event');
      }
    } catch (err) {
      alert('An error occurred while deleting the event');
    }
  };

  const handleLogout = () => {
    logout();
    onLogout();
  };

  const handleLimitRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    setRequestError('');

    if (requestedEventLimit <= currentEventLimit && requestedGuestLimit <= currentGuestLimit) {
      setRequestError('Requested limits must be higher than your current limits');
      return;
    }

    setRequestingLimit(true);

    try {
      const response = await createLimitRequest({
        requestedEventLimit,
        requestedGuestLimit,
        message: requestMessage.trim() || undefined,
      });

      if (response.success) {
        alert('Limit increase request submitted successfully. We will review it shortly.');
        setShowLimitRequestForm(false);
        setRequestMessage('');
      } else {
        setRequestError(response.error || 'Failed to submit request');
      }
    } catch (err) {
      setRequestError('An error occurred while submitting the request');
    } finally {
      setRequestingLimit(false);
    }
  };

  return (
    <div className="container dashboard-shell">
      <section className="page-hero card">
        <div className="page-hero__content">
          <span className="eyebrow">Organizer Dashboard</span>
          <h1 className="page-title">
            <Calendar size={28} />
            My Events
          </h1>
          <p className="page-copy">
            Create events, manage limits, and open the public guest lookup page for each event.
          </p>

          <div className="hero-badges">
            <div className="info-chip">
              <User size={16} />
              {user.name}
            </div>
            <div className="info-chip info-chip--muted">{user.email}</div>
          </div>
        </div>

        <div className="dashboard-hero-aside">
          <div className="stat-tile">
            <span>Events</span>
            <strong>{events.length}/{currentEventLimit}</strong>
          </div>
          <div className="stat-tile">
            <span>Guest Limit</span>
            <strong>{currentGuestLimit}</strong>
          </div>
          <button className="btn btn-secondary" onClick={handleLogout}>
            <LogOut size={16} />
            Logout
          </button>
        </div>
      </section>

      <section className="dashboard-actions">
        {!showLimitRequestForm ? (
          <button className="btn btn-secondary" onClick={() => setShowLimitRequestForm(true)}>
            <TrendingUp size={16} />
            Request Limit Increase
          </button>
        ) : (
          <div className="card panel-card">
            <div className="section-heading">
              <h2>Request Limit Increase</h2>
              <p>Ask for more event or guest capacity if your account needs to scale.</p>
            </div>

            <form onSubmit={handleLimitRequest}>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="eventLimit">Event Limit (current: {currentEventLimit})</label>
                  <input
                    id="eventLimit"
                    type="number"
                    className="login-input"
                    min={currentEventLimit + 1}
                    value={requestedEventLimit}
                    onChange={(e) => setRequestedEventLimit(parseInt(e.target.value, 10))}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="guestLimit">Guest Limit (current: {currentGuestLimit})</label>
                  <input
                    id="guestLimit"
                    type="number"
                    className="login-input"
                    min={currentGuestLimit + 1}
                    value={requestedGuestLimit}
                    onChange={(e) => setRequestedGuestLimit(parseInt(e.target.value, 10))}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="requestMessage">Message (optional)</label>
                <textarea
                  id="requestMessage"
                  className="login-input"
                  placeholder="Why do you need higher limits?"
                  value={requestMessage}
                  onChange={(e) => setRequestMessage(e.target.value)}
                  rows={3}
                  style={{ resize: 'vertical' }}
                />
              </div>

              {requestError && <div className="error-box">{requestError}</div>}

              <div className="button-row">
                <button type="submit" className="btn" disabled={requestingLimit}>
                  {requestingLimit ? 'Submitting...' : 'Submit Request'}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowLimitRequestForm(false);
                    setRequestError('');
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}
      </section>

      {events.length >= currentEventLimit && (
        <div className="alert-banner">
          <AlertCircle size={18} />
          <span>
            You have reached your maximum limit of {currentEventLimit} event{currentEventLimit > 1 ? 's' : ''}. Delete an existing event to create a new one.
          </span>
        </div>
      )}

      {!showCreateForm ? (
        <button
          className="btn dashboard-primary-action"
          onClick={() => setShowCreateForm(true)}
          disabled={events.length >= currentEventLimit}
        >
          <Plus size={18} />
          {events.length >= currentEventLimit ? 'Event Limit Reached' : 'Create New Event'}
        </button>
      ) : (
        <div className="card panel-card">
          <div className="section-heading">
            <h2>Create New Event</h2>
            <p>Set up the event basics now. You can add guests and floor plans after creation.</p>
          </div>

          <form onSubmit={handleCreateEvent}>
            <div className="form-group">
              <label htmlFor="eventName">Event Name *</label>
              <input
                id="eventName"
                type="text"
                className="login-input"
                placeholder="e.g., Sarah & John's Wedding"
                value={newEventName}
                onChange={(e) => setNewEventName(e.target.value)}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="eventDescription">Description</label>
              <textarea
                id="eventDescription"
                className="login-input"
                placeholder="Optional event description"
                value={newEventDescription}
                onChange={(e) => setNewEventDescription(e.target.value)}
                rows={3}
                style={{ resize: 'vertical' }}
              />
            </div>

            <div className="form-group">
              <label htmlFor="eventDate">Event Date</label>
              <input
                id="eventDate"
                type="date"
                className="login-input"
                value={newEventDate}
                onChange={(e) => setNewEventDate(e.target.value)}
              />
            </div>

            {error && <div className="error-box">{error}</div>}

            <div className="button-row">
              <button type="submit" className="btn" disabled={creating}>
                {creating ? 'Creating...' : 'Create Event'}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setShowCreateForm(false);
                  setNewEventName('');
                  setNewEventDescription('');
                  setNewEventDate('');
                  setError('');
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <div className="card empty-state-panel empty-state-panel--card">
          <div className="spinner spinner--small" />
          <span>Loading events...</span>
        </div>
      ) : events.length === 0 ? (
        <div className="card empty-state-panel empty-state-panel--card">
          <Calendar size={24} />
          <span>No events yet. Create your first event to get started.</span>
        </div>
      ) : (
        <div className="dashboard-event-grid">
          {events.map((event) => (
            <article key={event.id} className="card event-list-card">
              <div className="event-list-card__content" onClick={() => onEventSelect(event)}>
                <div className="event-list-card__header">
                  <h3>{event.name}</h3>
                  <span className="mono-chip">/event/{event.id}</span>
                </div>

                {event.description && (
                  <p className="event-list-card__description">{event.description}</p>
                )}

                <div className="event-list-card__meta">
                  {event.date && (
                    <span>
                      <Calendar size={14} />
                      {formatDisplayDate(event.date)}
                    </span>
                  )}
                  <span>Created {formatDisplayDate(event.createdAt)}</span>
                </div>
              </div>

              <div className="event-list-card__actions">
                <button className="btn" onClick={() => onEventSelect(event)}>
                  Open
                  <ArrowRight size={16} />
                </button>
                <button
                  className="btn btn-secondary btn-icon-danger"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteEvent(event.id);
                  }}
                  title="Delete event"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
